'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { TDynamicData } from '../../types/dynamic-data';

import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';
import { Carousel } from '../ui/carousel';
import { HeroInfiniteCarousel } from './hero-infinite-carousel';

export function Hero({ slides }: { slides: TDynamicData[] }) {
  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section className="flex w-full flex-col items-center justify-center gap-0">
      <div className={cn('w-full', 'relative')}>
        <Carousel
          options={options}
          enableLoop
          className={{
            container: 'w-full',
            wrapper: 'w-full',
            slide: 'w-full',
            main: 'w-full',
          }}
          slides={slides.map((slide, index) => (
            <SlideContent key={slide.name} slide={slide} index={index} />
          ))}
        />
        <div className="custom-container absolute bottom-[60px] left-1/2 z-10 ml-4 flex -translate-x-1/2 items-center justify-start gap-2 sm:bottom-16 xl:ml-0">
          {slides.map((_, index) => (
            <div
              key={index}
              className={cn(
                'h-2 w-2 rounded-full transition-all',
                selectedIndex === index ? 'w-6 bg-white' : 'w-2 bg-white/70',
              )}
            />
          ))}
        </div>
      </div>
      <HeroInfiniteCarousel />
    </section>
  );
}

function SlideContent({ slide, index }: { slide: TDynamicData; index?: number }) {
  const Title = index === 0 ? 'h1' : 'h2';
  const linkLabel = slide.fields?.[1]?.value;
  const linkHref = slide.fields?.[2]?.value;
  return (
    <div className={cn('custom-section', 'relative py-[60px] sm:py-16')}>
      <div
        className={cn(
          'custom-container',
          'relative z-10 flex flex-col items-start justify-start gap-[139px] sm:gap-40 lg:gap-[166px]',
        )}
      >
        <div className="invisible h-2 w-2" />
        <div className="flex w-full max-w-[858px] flex-col items-start justify-start gap-12">
          <div className="flex w-full flex-col items-start justify-start gap-6">
            <p className="font-heading text-lg font-medium text-primary-foreground sm:text-xl lg:text-2xl xl:text-[32px]">
              {slide.fields?.[0]?.value}
            </p>
            <Title className="line-clamp-2 font-heading text-[40px] font-medium leading-[80%] text-white sm:text-5xl sm:leading-[80%] md:text-6xl md:leading-[80%] lg:text-7xl lg:leading-[80%] xl:text-[96px] xl:leading-[80%]">
              {slide.name}
            </Title>
          </div>
          {linkLabel?.trim() && linkHref?.trim() ? (
            <Button asChild variant={'dark'} className="h-[60px] uppercase">
              <Link prefetch={false} href={linkHref}>
                {linkLabel}
              </Link>
            </Button>
          ) : null}
        </div>
        <div className="invisible h-2 w-2" />
      </div>
      <div className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-hero__image-overlay')} />
      <Image
        src={slide.image.url}
        alt={resolveImageAlt(slide?.image, slide?.name)}
        width={0}
        height={0}
        sizes="100vw"
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        priority={index === 0}
        fetchPriority={index === 0 ? 'high' : 'low'}
        loading={index === 0 ? 'eager' : 'lazy'}
      />
    </div>
  );
}
